/**
 * NVR 원격등록 템플릿 엑셀 기준 구역 생성 (순수 함수).
 *
 * 템플릿 1파일 = NVR 1대 = 구역 1개(또는 층이 섞이면 여러 개)이고, 시트의
 * `IP` / `CH NAME` 두 열이 곧 "이 구역에 어떤 카메라가 들어가는가" 의 정답이다.
 * CH NAME 은 `P{동}_B{층}_{구역}_{순번}` 이라 3번째 토막이 구역 번호다.
 *
 * 그래서 용량 기준(capacityZonePlanner)과 달리 추정할 게 없다 —
 * IP 로 배치된 CCTV 를 찾고, 이름을 CH NAME 으로 바꾸고, 같은 층·구역끼리 묶는다.
 * 파일에 다른 층 카메라가 섞여 있어도(예: `B1_9구역,B2_1구역`) 그 IP 는 현재
 * 그래프에 없으므로 자연히 빠진다.
 */

import { hullPoints } from './capacityZonePlanner'
import type { PlannedCapacityZone, PlannedCctvName, PlannerCamera, Pt } from '../model/types'

/** 템플릿 한 줄에서 뽑은 (IP → CH NAME). */
export interface NvrEntry {
  ip: string
  name: string
}

export interface NvrZonePlan {
  zones: PlannedCapacityZone[]
  names: PlannedCctvName[]
  /** 배치된 CCTV 를 못 찾은 템플릿 IP (다른 층이거나 미배치). */
  unmatchedIps: string[]
  /** CH NAME 이 규격(`P1_B1_2_6`)이 아니라 구역을 못 읽은 줄 수. */
  malformedNames: number
}

/** `P1_B1_2_6` → 층 `B1` / 구역 `2`. */
const CH_NAME_RE = /^P\d+_(B\d+)_(\d+)_(\d+)$/
const IPV4_RE = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function centroid(points: Pt[]): Pt {
  let sx = 0
  let sy = 0
  for (const p of points) {
    sx += p.x
    sy += p.y
  }
  return { x: sx / points.length, y: sy / points.length }
}

/**
 * 시트 행렬(header:1)에서 IP / CH NAME 열을 찾아 항목을 뽑는다.
 * 헤더 행 위치를 고정하지 않는 이유: 현장마다 안내 문구가 위에 몇 줄 붙는다.
 */
export function parseNvrSheetRows(rows: unknown[][]): NvrEntry[] {
  const text = (v: unknown) => (v == null ? '' : String(v).trim())

  let ipCol = -1
  let nameCol = -1
  let headerRow = -1
  for (let r = 0; r < rows.length && headerRow < 0; r += 1) {
    const cells = (rows[r] ?? []).map(c => text(c).toUpperCase())
    const ip = cells.indexOf('IP')
    const name = cells.indexOf('CH NAME')
    if (ip >= 0 && name >= 0) {
      ipCol = ip
      nameCol = name
      headerRow = r
    }
  }
  if (headerRow < 0) return []

  const entries: NvrEntry[] = []
  for (let r = headerRow + 1; r < rows.length; r += 1) {
    const row = rows[r] ?? []
    const ip = text(row[ipCol])
    const name = text(row[nameCol])
    if (!IPV4_RE.test(ip) || name === '') continue
    entries.push({ ip, name })
  }
  return entries
}

/**
 * 템플릿 항목을 배치된 카메라에 맞춰 구역·이름을 계획한다.
 * 그래프는 건드리지 않는다 — 호출자가 결과로 셀을 만들고 이름을 적용한다.
 */
export function planZonesFromNvrEntries(
  cameras: PlannerCamera[],
  entries: NvrEntry[],
  opts: { margin?: number } = {}
): NvrZonePlan {
  const margin = opts.margin ?? 0
  const byIp = new Map<string, PlannerCamera[]>()
  for (const cam of cameras) {
    const ip = (cam.ip ?? '').trim()
    if (!ip) continue
    const list = byIp.get(ip)
    if (list) list.push(cam)
    else byIp.set(ip, [cam])
  }

  // 층+구역 단위로 묶는다 — 한 NVR 에 두 층이 섞여도 구역 번호가 겹치지 않는다.
  const groups = new Map<string, { number: number; members: PlannerCamera[] }>()
  const names: PlannedCctvName[] = []
  const unmatchedIps: string[] = []
  let malformedNames = 0
  const seen = new Set<string>()

  for (const entry of entries) {
    const parts = CH_NAME_RE.exec(entry.name)
    if (!parts) {
      malformedNames += 1
      continue
    }
    const matched = byIp.get(entry.ip)
    if (!matched || matched.length === 0) {
      unmatchedIps.push(entry.ip)
      continue
    }
    const key = `${parts[1]}_${parts[2]}`
    const group = groups.get(key) ?? { number: Number(parts[2]), members: [] }
    for (const cam of matched) {
      if (seen.has(cam.id)) continue // 같은 IP 가 두 파일에 있으면 먼저 것을 따른다
      seen.add(cam.id)
      group.members.push(cam)
      names.push({ id: cam.id, name: entry.name })
    }
    groups.set(key, group)
  }

  const zones: PlannedCapacityZone[] = [...groups.values()]
    .filter(g => g.members.length > 0)
    .sort((a, b) => a.number - b.number)
    .map(g => {
      const center = centroid(g.members)
      return {
        number: g.number,
        name: `${g.number}구역`,
        center,
        radius: Math.max(...g.members.map(m => dist(m, center))) + margin,
        points: hullPoints(g.members, margin),
        memberIds: g.members.map(m => m.id),
        usedGb: 0,
      }
    })

  return { zones, names, unmatchedIps, malformedNames }
}
